import React, { useCallback } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';

import { LatestProcessCriteriaDTOTalentStatus, useFilterTalentsInfinite } from '@/api/generated';

import CountCard from './CountCard';

interface TalentStatusFilterFormValues {
  talents?: { latestProcess?: { talentStatus?: LatestProcessCriteriaDTOTalentStatus } };
  page?: number;
}

interface TalentStatusFilterProps {
  form: UseFormReturn<TalentStatusFilterFormValues>;
}

const TalentStatusFilter = ({ form }: TalentStatusFilterProps) => {
  const { t } = useTranslation('talents');

  const statusFilter: LatestProcessCriteriaDTOTalentStatus | '' =
    form.watch('talents.latestProcess.talentStatus') ?? '';

  const { data: statsData, isLoading } = useFilterTalentsInfinite(
    { page: 0, size: 10 },
    { includeStats: true },
    { query: { suspense: false } }
  );

  const countsData = statsData?.pages?.[0]?.stats;

  const handleChange = useCallback(
    (_: React.MouseEvent<HTMLElement>, newValue: LatestProcessCriteriaDTOTalentStatus | null) => {
      form.setValue('talents.latestProcess.talentStatus', newValue || undefined);
      form.setValue('page', 0);
    },
    [form]
  );

  return (
    <ToggleButtonGroup
      value={statusFilter}
      exclusive
      onChange={handleChange}
      sx={{
        display: 'flex',
        gap: 2,
        '& .MuiToggleButtonGroup-grouped': { flex: 1 },
        '& .MuiButtonBase-root': { p: 0 },
        '& .Mui-selected .CountCard-root': { backgroundColor: 'primary.100' }
      }}
    >
      <ToggleButton value={LatestProcessCriteriaDTOTalentStatus.GHOST}>
        <CountCard isLoading={isLoading} label={t('layouts.talents.unassignedCount')} count={countsData?.ghost} />
      </ToggleButton>
      <ToggleButton value={LatestProcessCriteriaDTOTalentStatus.REGISTERED}>
        <CountCard isLoading={isLoading} label={t('layouts.talents.registeredCount')} count={countsData?.registered} />
      </ToggleButton>
      <ToggleButton value={LatestProcessCriteriaDTOTalentStatus.IN_TEST}>
        <CountCard isLoading={isLoading} label={t('layouts.talents.inTestCount')} count={countsData?.inTest} />
      </ToggleButton>
      <ToggleButton value={LatestProcessCriteriaDTOTalentStatus.COMPLETED}>
        <CountCard isLoading={isLoading} label={t('layouts.talents.completedCount')} count={countsData?.completed} />
      </ToggleButton>
    </ToggleButtonGroup>
  );
};

export default TalentStatusFilter;
